import { Component, OnInit, AfterViewInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { ModalService } from '@niceltd/sol/modal';
import moment from 'moment';
import { CVLocalizationService } from 'src/app/reusables/services/cv-localization.service';
import { SubscriptionSchedulerService } from 'src/app/reusables/subscription-scheduler/subscription-scheduler.service';
import { ICvSubscriptionScheduleDataDates } from 'src/shared/models/subscriptions.model';
import { DashboardService } from '../../dashboard.service';
import { SelectValues, SubscriptionRecipientOption, TimePickerOption } from './subscription-modal.model';
import { SubscriptionModalService, SubscriptionOptions } from './subscription-modal.service';

/**
 * Constants for the subscription modal component
 */
const CONSTANTS = {
    TIME_STEP_MINUTES: 15,
    MINUTES_IN_DAY: 1440,
    DAYS_IN_WEEK: 7,
    DAYS_IN_MONTH: 31,
    DEFAULT_REPEAT_VALUE: 1,
    MILLISECONDS_IN_MINUTE: 60000
} as const;

/**
 * Subscription Modal Component
 * 
 * This component provides a comprehensive interface for creating and managing subscription schedules
 * for dashboards and reports. It supports various frequency options (daily, weekly, monthly),
 * time selection, recipient management with CXOne integration, and repeat options.
 * 
 * Key Features:
 * - Multi-frequency scheduling (daily, weekly, monthly)
 * - CXOne recipient integration with async loading
 * - Time picker with 15-minute increments
 * - Repeat scheduling with different intervals
 * 
 * @example
 * The component is typically opened via the SubscriptionModalService
 * const modalData = { dashboardKey: 'key', relatedId: 123, reportType: 0 };
 * this.subscriptionModalService.openModal(modalData);
 */
@Component({
    selector: 'cv-subscription-modal',
    templateUrl: './subscription-modal.component.html',
    styleUrls: ['./subscription-modal.component.scss']
})
export class SubscriptionModalComponent implements OnInit, AfterViewInit, OnDestroy {

    // #region Component State Properties
    
    /** Indicates if the subscription has been successfully saved */
    isSaved: boolean = false;
    
    /** Component initialization status */
    initialized: boolean = false;
    
    /** Loading states for different operations */
    isSaving: boolean = false;
    isLoading: boolean = false;
    
    /** Display control properties */
    showDeleteButton: boolean = false;
    dateFormat = CVAngularDateFormat;
    timeFormat = CVAngularTimeFormat;
    
    // #endregion
    
    // #region Email/Recipients Properties
    
    /** Email options loading and state management */
    emailOptionsLoaded: boolean = false;
    isEmailOptionsLoading: boolean = false;
    shouldDisableEmailOptions: boolean = true;
    
    /** Current user's CXOne UUID for notification recipient management */
    ownerUUID: string | null = null;
    
    /** Selected and available recipient options */
    selectedRecipients: SubscriptionRecipientOption[] = [];
    subscriptionRecipientOptions: SubscriptionRecipientOption[] | null = null;
    
    // #endregion
    
    // #region Schedule Configuration Properties
    
    /** Main schedule data model - contains all subscription configuration */
    schedule: ICvSubscriptionScheduleDataDates | null = null;
    
    /** Frequency selection properties */
    frequency: string = '';
    frequencies: SelectValues[] = [];
    selectedFrequency: SelectValues | null = null;
    
    /** Day selection for weekly/monthly schedules */
    daysOfWeek: SelectValues[] = [];
    daysOfMonth: SelectValues[] = [];
    daysOfMonthSelected: SelectValues[] = [];
    
    /** Recurring/repeat options */
    recurringDisplayValues: SelectValues[] = [];
    repeatHourlySelected: boolean = false;
    
    // #endregion
    
    // #region Time Picker Properties
    
    /** Time picker configuration with 15-minute increments */
    timePickerOptions: TimePickerOption[] = [];
    selectedTime: TimePickerOption | null = null;
    
    // #endregion
    
    // #region Localization Properties
    
    /** Localized text properties */
    emailPlaceholder: string = '';
    monthlyPlaceholder: string = '';
    lastText: string = '';
    
    // #endregion
    
    /** Modal configuration data passed from the service */
    modalData: SubscriptionOptions;

    /** Snapshot of the canonical schedule captured after initial load to detect changes */
    private originalCanonical: any = null;
    

    constructor(
        private readonly solModalService: ModalService,
        public readonly cvLocalizationService: CVLocalizationService,
        private readonly dashboardService: DashboardService,
        private readonly service: SubscriptionModalService,
        private readonly schedulerService: SubscriptionSchedulerService,
        private readonly cdr: ChangeDetectorRef,
    ) {}
    
    ngOnInit(): void {
        this.modalData = this.service._getModalData();
        this.initializeComponent();
        this.initialized = true;
    }
    
    private async initializeComponent(): Promise<void> { 
        await this.loadLocalizations();
        this.initializeDaysOfWeek();
        this.initializeDaysOfMonth();
        this.initializeRecurringDisplayValues();
        this.initializeFrequencies();
        this.selectedFrequency = this.frequencies[0];
        this.populateTimePickerOptions();
        
        this.resetScheduleModel();
    
        await this.getOwnerUUID();
    
        if (!this.modalData.subscriptionTitle)
            this.modalData.subscriptionTitle = "Subscription";
    
        if (this.modalData.subscriptionId === -1)
            this.modalData.subscriptionId = null;
    
        this.schedule.subscriptionId = this.modalData.subscriptionId;
    
       if (this.modalData.existingSchedule !== null && this.modalData.existingSchedule.ReportSubscriptionID !== -1) {
            await this.applyExistingSchedule();
        } else {
            await this.getSubscription();
        } 
    }
    
    /**
     * Angular lifecycle hook - runs after view initialization.
     * Handles recipient options loading to improve user experience by making the modal
     * available while recipient data is being fetched in the background.
     * 
     * @remarks
     * This approach allows users to interact with other parts of the modal immediately
     * while the recipient dropdown loads asynchronously.
     */
    ngAfterViewInit(): void {
        this.recipientsFirstLoad();
    }

    private async recipientsFirstLoad(): Promise<void> { 
        try {
            // Load recipient options asynchronously to improve modal responsiveness
            await this.loadRecipientOptions();
            
            // Only set selected recipients if we have both options and schedule recipients
            if (this.subscriptionRecipientOptions && this.schedule?.recipients) {
                this.selectedRecipients = this.subscriptionRecipientOptions.filter(
                    option => this.schedule.recipients.includes(option.value)
                );
            }
        } catch (error) {
            console.error('Failed to load recipient options in ngAfterViewInit:', error);
            // Keep the email options disabled if loading fails
            this.shouldDisableEmailOptions = true;
            return;
        } finally {
            this.isEmailOptionsLoading = false;
            this.shouldDisableEmailOptions = false;
            this.cdr.detectChanges();
        }
    }

    /**
     * Angular lifecycle hook - cleanup when component is destroyed.
     * Ensures proper cleanup of any resources to prevent memory leaks.
     */
    ngOnDestroy(): void {
        this.timePickerOptions = [];
        this.subscriptionRecipientOptions = null;
        this.selectedRecipients = [];
        this.daysOfWeek = [];
        this.daysOfMonth = [];
        this.daysOfMonthSelected = [];
        this.frequencies = [];
        this.recurringDisplayValues = [];
    }

    /**
     * Retrieves the current user's CXOne UUID for notification recipient management.
     * 
     * This UUID is used to:
     * - Determine if "Send to me" option should be checked
     * - Filter out the current user from recipient display when "Send to me" is enabled
     * - Ensure proper recipient management in CXOne integration
     * 
     * @returns Promise that resolves when UUID retrieval is complete
     * @throws Logs error if UUID retrieval fails but continues execution
     */
    private async getOwnerUUID(): Promise<void> {
        try {
            this.ownerUUID = await this.schedulerService.getCurrentUserCXOneUUID();
            
            if (!this.ownerUUID) {
                console.warn('No CXOne UUID found for current user. "Send to me" functionality may be limited.');
            }
        } catch (error) {
            console.error('Failed to retrieve current user CXOne UUID:', error);
            this.ownerUUID = null;
        }
    }

    /**
     * Loads all required localization texts for the modal.
     * 
     * Pre-loads localization sections and retrieves commonly used text strings
     * to avoid repeated service calls during component lifecycle.
     * 
     * @returns Promise that resolves when all localizations are loaded
     */
    private async loadLocalizations(): Promise<void> {
        const localizationPromises = [
            this.cvLocalizationService.GetSection('Subscription_Modal'),
            this.cvLocalizationService.GetSection('Objectives')
        ];
        await Promise.all(localizationPromises);
        // Load frequently used text strings with fallback values
        this.frequency = this.cvLocalizationService.getCachedText('Subscription_Modal', 'Frequency', 'Frequency');
        this.monthlyPlaceholder = this.cvLocalizationService.getCachedText('Subscription_Modal', 'Select_Items', 'Select Items');
        this.lastText = this.cvLocalizationService.getCachedText('Subscription_Modal', 'Last', 'Last');
        this.emailPlaceholder = this.cvLocalizationService.getCachedText('Subscription_Modal', 'Select_email_addresses', 'Select email addresses');
    }

    /**
     * Initializes the frequency options for subscription scheduling.
     * 
     * Sets up the available frequency types:
     * - Daily (value: 0) - Runs every day with repeat options
     * - Weekly (value: 1) - Runs on selected days of the week
     * - Monthly (value: 2) - Runs on selected days of the month
     * 
     * @private
     */
    private initializeFrequencies(): void {
        this.frequencies = [
            {
                value: 0,
                name: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Daily', 'Daily'),
                selected: false
            },
            {
                value: 1,
                name: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Weekly', 'Weekly'),
                selected: false
            },
            {
                value: 2,
                name: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Monthly', 'Monthly'),
                selected: false
            }
        ];
    }

    /**
     * Initializes the days of the week options for weekly frequency scheduling.
     * 
     * Creates localized day options with both full names and abbreviated forms.
     * Values are offset by 1 to match the scheduling system's day numbering.
     * 
     * @private
     */
    private initializeDaysOfWeek(): void {
        this.daysOfWeek = [
            {
                value: 1,
                name: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Sunday', 'Sunday'),
                shortName: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Sun_Abbr_3_Char', 'Sun'),
                selected: false
            },
            {
                value: 2,
                name: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Monday', 'Monday'),
                shortName: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Mon_Abbr_3_Char', 'Mon'),
                selected: false
            },
            {
                value: 3,
                name: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Tuesday', 'Tuesday'),
                shortName: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Tue_Abbr_3_Char', 'Tue'),
                selected: false
            },
            {
                value: 4,
                name: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Wednesday', 'Wednesday'),
                shortName: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Wed_Abbr_3_Char', 'Wed'),
                selected: false
            },
            {
                value: 5,
                name: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Thursday', 'Thursday'),
                shortName: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Thu_Abbr_3_Char', 'Thu'),
                selected: false
            },
            {
                value: 6,
                name: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Friday', 'Friday'),
                shortName: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Fri_Abbr_3_Char', 'Fri'),
                selected: false
            },
            {
                value: 7,
                name: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Saturday', 'Saturday'),
                shortName: this.cvLocalizationService.getCachedText('Subscription_Modal', 'Sat_Abbr_3_Char', 'Sat'),
                selected: false
            },
        ];
    }

    /**
     * Initializes the days of the month options for monthly frequency scheduling.
     * 
     * Creates day options from 1-31, with day 31 displayed as "Last" to handle
     * months with varying day counts (28, 29, 30, or 31 days).
     * 
     * @private
     */
    private initializeDaysOfMonth(): void {
        for (let i = 1; i <= CONSTANTS.DAYS_IN_MONTH; i++) {
            const displayName = (i !== CONSTANTS.DAYS_IN_MONTH) ? i.toString() : this.lastText;

            this.daysOfMonth.push({
                id: i,
                value: i,
                name: displayName,
            });
        }
    }

    /**
     * Initializes the recurring display value options for repeat scheduling.
     * 
     * Sets up time units for repeat intervals:
     * - Minutes (value: 0) - For very frequent updates
     * - Hours (value: 1) - For hourly repeats
     * - Days (value: 2) - For daily repeats
     * 
     * @private
     */
    private initializeRecurringDisplayValues(): void {
        this.recurringDisplayValues = [
            {
                value: 0,
                name: 'mins',
                selected: false,
            },
            {
                value: 1,
                name: 'hours',
                selected: false,
            },
            {
                value: 2,
                name: 'days',
                selected: false,
            },
        ];
    }

    /**
     * Loads and populates the email recipient options for the dropdown.
     * 
     * Fetches CXOne recipients and transforms them into the format expected by
     * the multi-select component. Includes proper error handling and prevents
     * duplicate loading attempts.
     * 
     * @returns Promise that resolves when recipient options are loaded
     * @throws Logs error if recipient loading fails
     * 
     * @remarks
     * This method includes debouncing logic to prevent multiple simultaneous calls
     * and provides fallback behavior if the service call fails.
     */
    private async loadRecipientOptions(): Promise<void> {
        // Prevent duplicate loading attempts
        if (this.emailOptionsLoaded || this.isEmailOptionsLoading) {
            return;
        }
        
        this.isEmailOptionsLoading = true;
        this.cdr.detectChanges();

        try {
            const cxOneRecipients = await this.schedulerService.getCXOneSubscriptionRecipients();
            
            if (!cxOneRecipients || cxOneRecipients.length === 0) {
                console.warn('No CXOne recipients found or empty response received');
                this.subscriptionRecipientOptions = [];
            } else {
                this.subscriptionRecipientOptions = cxOneRecipients.map(recipient => ({
                    name: recipient.FullName,
                    customTmpl: true,
                    value: recipient.UUID,
                    projectedData: {
                        primaryText: recipient.FullName,
                        secondaryText: `<${recipient.Email}>`,
                    }
                }));
            }
            
            this.emailOptionsLoaded = true;
        } catch (error) {
            console.error('Failed to load email recipient options:', error);
            this.subscriptionRecipientOptions = [];
        }
    }

    /**
     * Resets the schedule model to default values for new subscriptions.
     * 
     * Initializes all schedule properties with sensible defaults:
     * - Sets start date to today
     * - Enables "Send to me" by default
     * - Sets up daily repeat with default values
     * - Applies rounded time to the nearest 15-minute increment
     * 
     * @private
     */
    private resetScheduleModel(): void {
        const today = new Date();
        
        this.schedule = {
            scheduleID: null, // Will be assigned when subscription is created
            reportType: 0,
            relatedKey: this.modalData.dashboardKey,
            relatedID: null,
            startDate: today,
            endDate: null,
            exportType: 1, // Default export type
            frequency: 1, // Weekly frequency as default
            daysOfWeek: [],
            daysOfMonth: [],
            weeksOfMonth: [],
            firstExportTime: today,
            extraExportTimes: [],
            recipients: [],
            sendToMe: true, // Default to sending to current user
            repeat: true,
            repeatOptions: {
                isRepeat: true,
                repeatValue: CONSTANTS.DEFAULT_REPEAT_VALUE,
                repeatType: 2, // Days
                repeatEnd: moment().endOf("day").toDate() // End of today
            }
        };
        
        // Apply time rounding for better UX
        this.applyRoundedTimeToModel(today);
    }

    /**
     * Handles frequency selection changes and updates schedule configuration accordingly.
     * 
     * When frequency changes:
     * - Clears previous day selections
     * - For Daily (0): Sets up repeat scheduling with daily recurrence
     * - For Weekly/Monthly: Disables repeat and sets appropriate frequency
     * 
     * @public
     */
    onFrequencyChange(): void {
        if (!this.selectedFrequency || !this.schedule) {
            console.warn('Cannot change frequency: missing selected frequency or schedule');
            return;
        }
        
        // Reset day selections when frequency changes
        this.schedule.daysOfMonth = [];
        this.schedule.daysOfWeek = [];
        this.daysOfWeek.forEach(day => day.selected = false);
        this.daysOfMonthSelected = [];

        if (this.selectedFrequency.value === 0) {
            // Daily frequency with repeat options
            this.schedule.frequency = 1;
            this.schedule.repeat = true;
            this.schedule.repeatOptions = {
                isRepeat: true,
                repeatValue: CONSTANTS.DEFAULT_REPEAT_VALUE,
                repeatType: 2 // Days
            };
        } else {
            // Weekly or Monthly frequency
            this.schedule.frequency = this.selectedFrequency.value;
            this.schedule.repeat = false;
            this.schedule.repeatOptions.isRepeat = false;
            this.schedule.repeatOptions.repeatType = 1;
        }
    }

    /**
     * Updates the schedule's days of week based on current UI selections.
     * Maps the selected state of each day to a boolean array for the schedule model.
     * 
     * @public
     */
    onSelectedDaysChange(): void {
        if (!this.schedule) {
            console.warn('Cannot update selected days: schedule not initialized');
            return;
        }
        
        this.schedule.daysOfWeek = this.daysOfWeek.map(day => day.selected);
    }

    /**
     * Updates the schedule's days of month based on current selections.
     * Converts the selected days array to a boolean array indexed by day number.
     * 
     * @public
     */
    onChangeSelectedDOM(): void {
        if (!this.schedule) {
            console.warn('Cannot update selected days of month: schedule not initialized');
            return;
        }
        
        const selectedDoM = new Set(this.daysOfMonthSelected.map(obj => obj.value));
        this.schedule.daysOfMonth = this.daysOfMonth.map((_day, index) => selectedDoM.has(index + 1));
    }

    /**
     * Checks if no days of the week are currently selected.
     * 
     * This method is used for form validation to ensure at least one day
     * is selected for weekly frequency schedules.
     * 
     * @returns true if no days are selected, false if any day is selected
     * @public
     */
    checkSelectedWeeks(): boolean {
        return !this.daysOfWeek.some(day => day.selected);
    }

    async getSubscription() {
        this.schedule.relatedID ??= this.modalData.relatedId;
        this.schedule.relatedKey ??= this.modalData.dashboardKey;

        //Try to retrieve a subscription. If one doesn't exist, an empty object with an ID of -1 will be returned.
        if (this.modalData.relatedId && this.modalData.dashboardKey) {
            this.isLoading = true;

            // This factory function gets a combination object containing both the ReportSubscription and ReportSubscriptionSchedule
            const response =
                await this.schedulerService.getSubscriptionScheduleByRelatedKeyAndID(this.modalData.dashboardKey, this.modalData.relatedId);

            if (response.ReportSubscription.ReportSubscriptionID === -1) {
                this.isLoading = false;
                return;
            }

            this.showDeleteButton = true;

            this.schedule.sendToMe = response.ReportSubscription.SendToOwner;
            this.schedule.title = response.ReportSubscription.ReportSubscriptionName;
            this.schedule.folder = response.ReportSubscription.Folder;
            this.schedule.subscriptionId = response.ReportSubscription.ReportSubscriptionID;
            this.schedule.reportType = response.ReportSubscription.ReportType;
            this.schedule.endDate = this.modalData.embedMode ? moment(this.modalData.existingSchedule.EndDate).toDate()
                : moment(response.ReportSubscription.EndDate).toDate();
            this.schedule.exportType = response.ReportSubscription.ExportType;
            if (response.ReportSubscriptionSchedule != null) {
                this.schedule.startDate = moment(response.ReportSubscriptionSchedule.StartDateTime).toDate();
                this.schedule.frequency = response.ReportSubscriptionSchedule.ScheduleType;
                this.schedule.firstExportTime = moment(response.ReportSubscriptionSchedule.StartDateTime).toDate();
                this.applyRoundedTimeToModel(this.schedule.firstExportTime);
                this.schedule.extraExportTimes = (response.ReportSubscriptionSchedule.ExtraExportTimes !== undefined)
                    ? response.ReportSubscriptionSchedule.ExtraExportTimes.map(d => moment(d).toDate()) : [];
            } else {
                this.schedule.startDate = new Date();
                this.schedule.frequency = 1;
                this.schedule.firstExportTime = new Date();
                this.schedule.extraExportTimes = [];
            }
            this.schedule.daysOfWeek = [];
            this.schedule.daysOfMonth = [];
            this.schedule.weeksOfMonth = [];
            this.schedule.recipients = response.ReportSubscription.RecipientEmails;

            // If sendToMe is checked, remove the display value.
            if (this.schedule.sendToMe && this.ownerUUID) {
                this.schedule.recipients = this.schedule.recipients.filter(email => email !== this.ownerUUID);
            }
            // If repeat is checked, it uses the given values. Otherwise, it uses blank values.
            if (response.ReportSubscriptionSchedule
                && response.ReportSubscriptionSchedule.RecurringMinutes > 0
                && response.ReportSubscriptionSchedule.RecurringDisplayType != null
                && response.ReportSubscriptionSchedule.EndDateTime != null) {
                this.schedule.repeatOptions.isRepeat = true;
                this.schedule.repeatOptions.repeatType = response.ReportSubscriptionSchedule.RecurringDisplayType;
                // If repeat type is hour or day, convert the raw value (minutes) to an integer value for the appropriate unit.
                if (this.schedule.repeatOptions.repeatType === 1) {
                    this.schedule.repeatOptions.repeatValue = (response.ReportSubscriptionSchedule.RecurringMinutes / 60) || 0;
                } else if (this.schedule.repeatOptions.repeatType === 2) {
                    this.schedule.repeatOptions.repeatValue = (response.ReportSubscriptionSchedule.RecurringMinutes / 1440) || 0;
                } else {
                    this.schedule.repeatOptions.repeatValue = response.ReportSubscriptionSchedule.RecurringMinutes;
                }
                // This is done to avoid UTC to Local (in the endpoint) getting confused with daylight saving
                const endDateTime = moment(response.ReportSubscriptionSchedule.EndDateTime).toDate();
                const repeatEnd = moment().seconds(0)
                    .hours(endDateTime.getHours())
                    .minutes(endDateTime.getMinutes());
                this.schedule.repeatOptions.repeatEnd = repeatEnd.toDate();
            } else {
                this.schedule.repeatOptions.isRepeat = false;
                this.schedule.repeatOptions.repeatValue = 1;
                this.schedule.repeatOptions.repeatType = 2; // days
                this.schedule.repeatOptions.repeatEnd = moment().endOf("day").toDate();
            }

            // Map the multi-select values to the corresponding display model objects.
            // Convert from one int to a list of enum-ints (see enums in framework for highest int value)
            // daysOfWeek
            this.schedule.daysOfWeek = [];
            this.daysOfMonthSelected = [];
            if (response.ReportSubscriptionSchedule) {
                const dowLimit = Math.pow(2, 7);
                for (let i = 1, n = 0; i < dowLimit; i *= 2, n++) {
                    if ((response.ReportSubscriptionSchedule.DaysOfWeek & i) === i) {
                        this.schedule.daysOfWeek[n] = true;
                        this.daysOfWeek[n].selected = true;
                    } else {
                        this.schedule.daysOfWeek[n] = false;
                        this.daysOfWeek[n].selected = false;
                    }
                }
                this.daysOfWeek = this.daysOfWeek.slice();

                // daysOfMonth
                const domLimit = Math.pow(2, 31);
                for (let i = 1, n = 0; i < domLimit; i *= 2, n++) {
                    if ((response.ReportSubscriptionSchedule.DaysOfMonth & i) === i) {
                        this.schedule.daysOfMonth[n] = true;
                        this.daysOfMonthSelected.push(this.daysOfMonth[n]);
                    } else {
                        this.schedule.daysOfMonth[n] = false;
                    }
                }
            }

            this.selectedFrequency = this.frequencies.find((frequency) => frequency.value === this.schedule.frequency);
            this.isLoading = false;
            // capture canonical snapshot after load for dirty check
            if (this.schedule) {
                this.originalCanonical = this.canonicalize(this.schedule);
            }
        }
    }


    async applyExistingSchedule() {
        this.isLoading = true;

        const response = await this.schedulerService.getSubscriptionSchedule(
            this.modalData.existingSchedule.ReportSubscriptionID,
            this.modalData.existingSchedule.RelatedID,
            this.modalData.existingSchedule.RelatedKey);

        this.schedule.subscriptionId = this.modalData.existingSchedule.ReportSubscriptionID;
        this.schedule.scheduleID = response.ReportSubscriptionScheduleID;
        this.schedule.relatedID = this.modalData.relatedId;
        this.schedule.startDate = moment(response.StartDateTime).toDate();
        this.schedule.endDate = (response.EndDate != null) ? moment(response.EndDate).toDate()
            : moment(this.modalData.existingSchedule.EndDate).toDate();
        this.schedule.exportType = this.modalData.existingSchedule.ExportType ?? this.schedule.exportType;
        this.schedule.frequency = response.ScheduleType;
        this.schedule.daysOfWeek = [];
        this.schedule.daysOfMonth = [];
        this.schedule.weeksOfMonth = [];
        this.schedule.firstExportTime = moment(response.StartDateTime).toDate();
        this.applyRoundedTimeToModel(this.schedule.firstExportTime);
        this.schedule.extraExportTimes = (response.ExtraExportTimes != null) ? response.ExtraExportTimes.map(d => moment(d).toDate()) : [];
        this.schedule.recipients = (this.modalData.existingSchedule.RecipientEmails != null)
            ? [...this.modalData.existingSchedule.RecipientEmails]
            : this.modalData.existingSchedule.Recipients?.map(r => r.EmailAddress);
        this.schedule.sendToMe = this.schedule.recipients.includes(this.ownerUUID);

        // If sendToMe is checked, remove the display value.
        if (this.schedule.sendToMe && this.ownerUUID) {
            this.schedule.recipients = this.schedule.recipients.filter(email => email !== this.ownerUUID);
        }

        // Repeat Options
        if (response.RecurringMinutes > 0) {
            this.schedule.repeat = true;
            this.schedule.repeatOptions.isRepeat = true;
            this.schedule.repeatOptions.repeatType = response.RecurringDisplayType;
            // If repeat type is hour or day, convert the raw value (minutes) to an integer value for the appropriate unit.
            if (this.schedule.repeatOptions.repeatType === 1) {
                this.schedule.repeatOptions.repeatValue = (response.RecurringMinutes / 60) || 0;
            } else if (this.schedule.repeatOptions.repeatType === 2) {
                this.schedule.repeatOptions.repeatValue = (response.RecurringMinutes / 1440) || 0;
            } else {
                this.schedule.repeatOptions.repeatValue = response.RecurringMinutes;
            }
            const endDateTime = moment(response.EndDateTime).toDate();
            const repeatEnd = moment().seconds(0)
                .hours(endDateTime.getHours())
                .minutes(endDateTime.getMinutes());
            this.schedule.repeatOptions.repeatEnd = repeatEnd.toDate();
        } else {
            this.schedule.repeatOptions.isRepeat = false;
            this.schedule.repeatOptions.repeatValue = 1;
            this.schedule.repeatOptions.repeatType = 2; // days
            this.schedule.repeatOptions.repeatEnd = moment().endOf("day").toDate();
        }

        // Map the multi-select values to the corresponding display model objects.
        // Convert from one int to a list of enum-ints (see enums in framework for highest int value)
        // daysOfWeek
        this.schedule.daysOfWeek = [];
        const dowLimit = Math.pow(2, 7);
        for (let i = 1, n = 0; i < dowLimit; i *= 2, n++) {
            if ((response.DaysOfWeek & i) === i) {
                this.schedule.daysOfWeek[n] = true;
                this.daysOfWeek[n].selected = true;
            } else {
                this.schedule.daysOfWeek[n] = false;
                this.daysOfWeek[n].selected = false;
            }
        }
        this.daysOfWeek = this.daysOfWeek.slice();

        // daysOfMonth
        this.daysOfMonthSelected = [];
        const domLimit = Math.pow(2, 31);
        for (let i = 1, n = 0; i < domLimit; i *= 2, n++) {
            if ((response.DaysOfMonth & i) === i) {
                this.schedule.daysOfMonth[n] = true;
                this.daysOfMonthSelected.push(this.daysOfMonth[n]);
            } else {
                this.schedule.daysOfMonth[n] = false;
            }
        }

        if (this.schedule.repeatOptions.isRepeat && this.schedule.repeatOptions.repeatType === 2) {
            this.selectedFrequency = this.frequencies.find(frequency => frequency.value === 0);
        } else {
            this.selectedFrequency = this.frequencies.find(frequency => frequency.value === this.schedule.frequency);
            this.repeatHourlySelected = this.schedule.repeatOptions.isRepeat && this.schedule.repeatOptions.repeatType === 1;
        }

        this.schedule = this.schedulerService.objectifyTheScheduleDates(this.schedule);

        this.isLoading = false;
        // capture canonical snapshot after load for dirty check
        if (this.schedule) {
            this.originalCanonical = this.canonicalize(this.schedule);
        }
    }

    /**
     * Handles changes to the repeat hourly checkbox option.
     * 
     * When enabled, sets up hourly repeat scheduling.
     * When disabled, reverts to no repeat with daily type fallback.
     * 
     * @public
     */
    onRepeatHourlyChanged(): void {
        if (!this.schedule) {
            console.warn('Cannot change repeat hourly: schedule not initialized');
            return;
        }
        
        if (this.repeatHourlySelected) {
            this.schedule.repeat = true;
            this.schedule.repeatOptions = {
                isRepeat: true,
                repeatType: 1, // Hours
                repeatValue: CONSTANTS.DEFAULT_REPEAT_VALUE
            };
        } else {
            this.schedule.repeat = false;
            this.schedule.repeatOptions = {
                isRepeat: false,
                repeatType: 2 // Days fallback
            };
        }
    }

    /**
     * Gets the localized text for the "Repeat hourly" option.
     * 
     * @returns Localized string for repeat hourly text
     * @public
     */
    getRepeatHourlyText(): string {
        return this.cvLocalizationService.getCachedText('Subscription_Modal', 'Repeat', 'Repeat hourly');
    }

    /**
     * Gets the localized text for the "Send to me" option.
     * 
     * @returns Localized string for send to me text
     * @public
     */
    getSendToMeText(): string {
        return this.cvLocalizationService.getCachedText('Subscription_Modal', 'Send_To_Me', 'Send to me');
    }


    /**
     * Handles modal cancellation.
     * 
     * Resets the schedule model to clean state and closes the modal without saving.
     * Only closes if the subscription hasn't been saved already.
     * 
     * @public
     */
    cancel(): void {
        this.resetScheduleModel();
        
        if (!this.isSaved) {
            this.solModalService.close(null);
        }
    }

    async save() {
        if (this.isSaving)
            return;

        this.isLoading = true;

        if (this.selectedFrequency.value === 0) {
            this.daysOfWeek.forEach(day => day.selected = true);
            this.onSelectedDaysChange();
        }

        if (!this.modalData.embedMode){
            this.schedule.reportType = this.modalData.reportType;
            if( this.schedule.description == null)
                this.schedule.description = '';

            //When not in embedMode, it's assumed that the title needs to be manually set. If this.title is defined, use it. Otherwise, use a default value based on report type.
            if(this.modalData.subscriptionTitle != null) {
                this.schedule.title = this.modalData.subscriptionTitle;
            } else {
                switch (this.modalData.reportType) { //Based on ReportType enum in framework.
                    case 0: //Dashboard
                        this.schedule.title = "Dashboard Subscription";
                        break;
                    case 1: //Module
                        this.schedule.title = "Module Subscription";
                        break;
                    default:
                        break;
                }
            }

            if (this.schedule.subscriptionId == null) {
                try
                {
                   const response = await this.schedulerService.createSubscriptionSchedule(this.schedule, true);

                    if (this.modalData.reportType === 0) {
                        // update dashboard slideout (for the icon)
                        this.dashboardService.availableDashboards.find(dash => {
                            if (dash.DashboardKey === this.schedule.relatedKey) {
                                dash.IsSubscribed = true;
                            }
                        });

                        this.dashboardService.availableDashboardsChanged.emit();
                    }

                    this.isSaved = true;
                } catch (err) {
                    this.isSaving = false;
                    console.error('[CreateSubscriptionSchedule] Failed to save new subscription.', err);
                    err = err.data || err;
                    let errorMessage = "An unexpected error occurred";
                    if (err && err.warnings && typeof (err.warnings[0]) === "string" && err.warnings[0].length > 0)
                        errorMessage = err.warnings[0];
                    window['showErrorDialog'](errorMessage, "Subscription Error");
                }
            } else {
                try
                {
                    const response = await this.schedulerService.updateSubscriptionSchedule(this.schedule, true);
                    this.isSaved = true;
                } catch (err) {
                    console.error("Error updating subscription schedule", err);
                    err = err.data || err;
                    let errorMessage = "An unexpected error occurred";
                    if (err && err.warnings && typeof (err.warnings[0]) === "string" && err.warnings[0].length > 0)
                        errorMessage = err.warnings[0];
                    window['showErrorDialog'](errorMessage, "Subscription Error");
                }
            }
        }

        this.isLoading = false;
        const newSubSchedule = 
            await this.schedulerService.getSubscriptionScheduleByRelatedKeyAndID(this.schedule.relatedKey, this.schedule.relatedID);
        this.solModalService.close(newSubSchedule);
    }

    /**
     * Populates the `timePickerOptions` array with time slots in 15-minute increments,
     * formatted as label-value pairs, and sets the `selectedTime` to the next available
     * slot based on the current time.
     *
     * - Each option's value is the number of minutes past midnight, except for midnight (0),
     *   which uses the string `'zero'` as a special case.
     * - The function rounds the current time up to the next 15-minute slot, wrapping to midnight if necessary.
     * - The `selectedTime` is set to the matching option for the rounded time, or the first option if not found.
     *
     * @remarks
     * This method is intended for use in time picker components where users select a time of day.
     * The special 'zero' value handles midnight edge cases in the time picker logic.
     *
     * @public
     */
    public populateTimePickerOptions(): void {
        this.timePickerOptions = [];
        
        for (let t = 0; t < CONSTANTS.MINUTES_IN_DAY; t += CONSTANTS.TIME_STEP_MINUTES) {
            const label = this.getFormattedTime(t);
            const value: number | 'zero' = (t === 0 ? 'zero' : t);
            this.timePickerOptions.push({ label, value });
        }

        // Set default selection to next 15-minute increment
        const now = new Date();
        const totalMins = now.getHours() * 60 + now.getMinutes();

        let rounded = Math.ceil(totalMins / CONSTANTS.TIME_STEP_MINUTES) * CONSTANTS.TIME_STEP_MINUTES;
        if (rounded >= CONSTANTS.MINUTES_IN_DAY) {
            rounded = 0;
        }

        const selectValue: number | 'zero' = (rounded === 0 ? 'zero' : rounded);
        this.selectedTime = this.timePickerOptions.find(option => option.value === selectValue)
            || this.timePickerOptions[0];
    }

    /**
     * Converts a number of minutes into a formatted 12-hour time string with AM/PM.
     *
     * @param totalMinutes - The number of minutes to convert (e.g., 0 = 12:00 AM, 780 = 1:00 PM)
     * @returns A string representing the formatted time in "hh:mm AM/PM" format
     *
     * @example
     * ```typescript
     * getFormattedTime(0);    // "12:00 AM"
     * getFormattedTime(780);  // "1:00 PM"
     * getFormattedTime(1439); // "11:59 PM"
     * ```
     * 
     * @public
     */
    public getFormattedTime(totalMinutes: number): string {
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const period = hours < 12 || hours === 24 ? 'AM' : 'PM';

        const formattedHours = hours % 12 || 12;
        const formattedMinutes = minutes.toString().padStart(2, '0');

        return `${formattedHours}:${formattedMinutes} ${period}`;
    }


    /**
     * Handles changes from the time picker component.
     * 
     * When the user selects a new time, this method extracts the total minutes from the selected value,
     * calculates the corresponding hours and minutes, and updates the `schedule.firstExportTime` property.
     * The date is set to today, and the time is set to the selected hours and minutes, with seconds set to zero.
     * 
     * @remarks
     * - If `selectedTime` is `null` or `schedule` is not initialized, the method returns early
     * - If `selectedTime.value` is `'zero'`, it is treated as 0 minutes (midnight)
     * - Uses moment.js for consistent date/time handling
     * 
     * @public
     */
    public onTimePickerChange(): void {
        if (!this.selectedTime || !this.schedule) {
            console.warn('Cannot change time: missing selected time or schedule not initialized');
            return;
        }

        const raw = this.selectedTime.value;
        const totalMins = (raw === 'zero' ? 0 : raw);
        const hours = Math.floor(totalMins / 60);
        const minutes = totalMins % 60;

        this.schedule.firstExportTime = moment()
            .hours(hours)
            .minutes(minutes)
            .seconds(0)
            .toDate();
    }

    /** 
     * Rounds a Date up to the next time step (ceiling operation), wrapping past midnight.
     * 
     * @param date - The date to round
     * @param step - The step size in minutes (defaults to 15)
     * @returns The rounded time as minutes-of-day (0–1439)
     * 
     * @example
     * ```typescript
     * // 10:37 AM becomes 10:45 AM (645 minutes)
     * const rounded = roundDateToNextStep(new Date('2023-01-01 10:37:00'), 15);
     * console.log(rounded); // 645
     * ```
     * 
     * @private
     */
    private roundDateToNextStep(date: Date, step: number = CONSTANTS.TIME_STEP_MINUTES): number {
        const total = date.getHours() * 60 + date.getMinutes();
        let rounded = Math.ceil(total / step) * step;
        return rounded >= CONSTANTS.MINUTES_IN_DAY ? 0 : rounded;
    }


    /**
     * Given a Date, round it and then:
     *  • update schedule.firstExportTime to that rounded time (today’s date),
     *  • compute & set selectedTime so the dropdown highlights it.
     */
    private applyRoundedTimeToModel(date: Date) {
        const mins = this.roundDateToNextStep(date, 15);

        const hours = Math.floor(mins / 60);
        const minutes = mins % 60;
        this.schedule.firstExportTime = moment()
            .hours(hours)
            .minutes(minutes)
            .seconds(0)
            .toDate();

        const selectValue: number | 'zero' = mins === 0 ? 'zero' : mins;
        this.selectedTime = this.timePickerOptions.find(o => o.value === selectValue)
            || this.timePickerOptions[0];
    }

    /**
     * Handles changes in email recipient selection from the multi-select component.
     * 
     * Updates the schedule's recipients list based on the selected email options.
     * Maintains synchronization between the UI selection and the schedule model.
     * 
     * @param selected - An array of selected email options from the multi-select component
     * 
     * @remarks
     * This method extracts UUIDs from the selected options and updates the schedule.recipients array.
     * The selectedRecipients array is also updated to maintain UI state consistency.
     * 
     * @public
     */
    onRecipientSelectionChange(selected: SubscriptionRecipientOption[]): void {
        if (!this.schedule) {
            console.warn('Cannot change email selection: schedule not initialized');
            return;
        }
        
        // Always update the selected recipients, even if empty
        this.selectedRecipients = selected || [];
        
        if (selected && selected.length > 0) {
            this.schedule.recipients = selected.map(option => option.value);
        } else {
            // Clear recipients if no selection
            this.schedule.recipients = [];
        }
    }

    /** Indicates whether current schedule differs from the originally loaded one */
    get isDirty(): boolean {
        if (!this.schedule) return false;
        if (!this.originalCanonical) return true;
        const current = this.canonicalize(this.schedule);
        return !this.deepEqual(current, this.originalCanonical);
    }

    /** Returns minutes-of-day for a Date (0..1439) */
    private minutesOfDay(date: Date | null | undefined): number | null {
        if (!date) return null;
        const d = new Date(date);
        return d.getHours() * 60 + d.getMinutes();
    }

    /**
     * Canonical representation of a schedule, retaining only identity-defining fields
     * and normalizing time-based fields to minutes-of-day to ensure stable equality.
     */
    private canonicalize(schedule: ICvSubscriptionScheduleDataDates) {
        const firstExportTimeMins = this.minutesOfDay(schedule.firstExportTime) ?? 0;
        const extraExportTimesMins = (schedule.extraExportTimes || [])
            .map(t => this.minutesOfDay(t) ?? 0)
            .sort((a, b) => a - b);

        // Normalize recipients: exclude ownerUUID when sendToMe is true to avoid double counting
        const recipients = (schedule.recipients || [])
            .filter(r => !(schedule.sendToMe && this.ownerUUID && r === this.ownerUUID))
            .slice()
            .sort((a, b) => a.localeCompare(b));

        // Normalize repeat to minutes
        let repeatMinutes = 0;
        if (schedule.repeat && schedule.repeatOptions?.isRepeat) {
            const type = schedule.repeatOptions.repeatType; // 0=min,1=hr,2=day
            const value = schedule.repeatOptions.repeatValue || 0;
            if (type === 0) repeatMinutes = value;
            else if (type === 1) repeatMinutes = value * 60;
            else if (type === 2) repeatMinutes = value * 1440;
        }
        const repeatEndMins = this.minutesOfDay(schedule.repeatOptions?.repeatEnd);

        // Ensure boolean arrays are copied by value
        const daysOfWeek = (schedule.daysOfWeek || []).map(Boolean);
        const daysOfMonth = (schedule.daysOfMonth || []).map(Boolean);
        const weeksOfMonth = (schedule.weeksOfMonth || []).map(Boolean);

        return {
            frequency: schedule.frequency,
            daysOfWeek,
            daysOfMonth,
            weeksOfMonth,
            firstExportTimeMins,
            extraExportTimesMins,
            exportType: schedule.exportType,
            sendToMe: !!schedule.sendToMe,
            recipients,
            repeat: !!schedule.repeat,
            repeatMinutes,
            repeatEndMins,
        };
    }

    /** Simple deep equality using JSON after canonicalization */
    private deepEqual(a: any, b: any): boolean {
        try {
            return JSON.stringify(a) === JSON.stringify(b);
        } catch {
            return false;
        }
    }
}
